import {
  emotionStyled,
  tokens,
  mobileTextControlStyleAtom,
} from '@packages/utilities/styled';

import {
  disabledSelector,
  errorSelector,
  focusedSelector,
  hoverSelector,
  activeSelector,
} from '../../utils';

import IconButton from '../IconButton';

export const TextInputWrapperView = emotionStyled('div')(
  {
    backgroundColor: `$uiInputBackground`,
    border: '1px solid',
    borderColor: '$uiInputBorderColor',
    borderRadius: '$2',
    boxShadow: '$inputInner',
    color: '$textPrimary',
    display: 'flex',
    flex: '1 0 0',
    position: 'relative',
    width: '100%',
    padding: tokens.ui.input.padding,
    [disabledSelector('&')]: {
      backgroundColor: '$backgroundInactive',
      borderColor: '$border2',
      cursor: 'not-allowed',
      opacity: tokens.opacities.disabled,
    },
    [`&${errorSelector}`]: {
      borderColor: '$redBorder',
    },
    [`&${hoverSelector}`]: {
      borderColor: '$uiInputBorderColorHover',
    },
    [`&${focusedSelector}:not(${errorSelector})`]: {
      borderColor: '$blueBorder',
    },
  },
  {
    componentName: 'TextInput',
    variants: {
      isBranded: {
        true: {
          padding: '$3 $2',
        },
      },
    },
  }
);

export const TextInputContentView = emotionStyled('div')(
  {
    display: 'flex',
    gap: '$0.5',
    alignItems: 'center',
    width: '100%',
  },
  {
    componentName: 'TextInputContent',
  }
);

export const TextInputPrefixSuffixView = emotionStyled('div')(
  {
    color: '$textSecondary',
    position: 'relative',
    fontSize: '$body',
    display: 'flex', // constrain the width of the prefix/suffix
  },
  {
    variants: {
      isBranded: {
        true: {
          fontFamily: '$brandedBody',
          fontSize: '$brandedBody',
          lineHeight: '$brandedBody',
        },
      },
    },
  }
);

export const TextInputView = emotionStyled('input')(
  {
    appearance: 'none',
    background: 'transparent',
    border: 'none',
    color: '$textPrimary',
    display: 'block',
    flex: 1,
    fontFamily: '$sansSerif',
    fontSize: '$body',
    // Disables the Inter font's `liga` and `calt` features so values like
    // `1920x1080` render literally (see CONT-6296). The shipped Inter build
    // defines this substitution under `liga`; newer builds use `calt`.
    fontVariantLigatures: 'no-common-ligatures no-contextual',
    lineHeight: '$body',
    outline: 'none',
    position: 'relative',
    resize: 'none',
    width: '100%',
    padding: 0,
    '&::placeholder': {
      color: '$textDimmed',
    },

    [`${TextInputWrapperView}[data-disabled="true"] &`]: {
      cursor: 'not-allowed',
    },
    ':-webkit-autofill': {
      // These properties are necessary to make the user agent styles for autofill disappear (like turning the input background yellow and changing the text color)
      backgroundClip: 'text',
      WebkitTextFillColor: tokens.colors.textPrimary,
      caretColor: tokens.colors.textPrimary,
    },
  },
  {
    componentName: 'TextInputView',
    composes: [mobileTextControlStyleAtom],
    variants: {
      isBranded: {
        true: {
          fontFamily: '$brandedBody',
          fontSize: '$brandedBody',
          lineHeight: '$brandedBody',
          '&::placeholder': {
            fontFamily: '$brandedBody',
            fontSize: '$brandedBody',
            lineHeight: '$brandedBody',
          },
        },
      },
    },
  }
);

export const TextInputClearButtonView = emotionStyled(IconButton)(
  {
    padding: 0,
    marginLeft: tokens.space[0.5],

    [`&${hoverSelector}`]: {
      background: 'none',
    },

    [`&${activeSelector}`]: {
      background: 'none',
    },
  },
  {
    componentName: 'TextInputClearButton',
  }
);
